import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Alert,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { Title } from "react-admin";
import { getJson, postJson, putJson } from "../../utils/api";
import { FeedbackGroup, LessonDetail, QuestionNode } from "../../models";
import { QuestionCard } from "./QuestionCard";

const ADMIN = "/admin";

const EMPTY_NODE: () => QuestionNode = () => ({
  id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  question_text: "",
  question_type: "MULTIPLE_CHOICE",
  options: [
    { id: `o-${Date.now()}-1`, text: "", is_correct: true },
    { id: `o-${Date.now()}-2`, text: "", is_correct: false },
  ],
});

interface CreateState {
  title: string;
  type: string;
  unitId: string;
  curriculumId: string;
}

export const LessonEditor: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const createState =
    lessonId === "new" ? (location.state as CreateState) : null;
  const isNew = lessonId === "new";

  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [title, setTitle] = useState(createState?.title ?? "");
  const [type, setType] = useState(createState?.type ?? "STANDARD");
  const [nodes, setNodes] = useState<QuestionNode[]>([]);
  const [feedbackGroups, setFeedbackGroups] = useState<FeedbackGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const fetchLesson = useCallback(async () => {
    if (!lessonId || isNew) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const resp = await getJson(`${ADMIN}/lessons/${lessonId}`);
      const { data } = await resp.json();
      setLesson(data);
      setTitle(data.title);
      setType(data.type);
      setNodes(data.nodes ?? []);
      setDirty(false);
    } catch (e) {
      console.error("Failed to fetch lesson", e);
    } finally {
      setLoading(false);
    }
  }, [lessonId, isNew]);

  // Fetch feedback groups for the curriculum (need to figure out curriculum from lesson context)
  const fetchFeedbackGroups = useCallback(async () => {
    // We don't have direct curriculum_id from lesson, so fetch all curricula and
    // find which one owns this lesson. For now, we try a simpler approach: fetch
    // all curricula and their feedback groups.
    try {
      const curResp = await getJson(`${ADMIN}/curricula`);
      const { data: allCur } = await curResp.json();
      const allFgs: FeedbackGroup[] = [];
      for (const cur of allCur ?? []) {
        try {
          const fgResp = await getJson(
            `${ADMIN}/curricula/${cur.id}/feedback-groups`
          );
          const { data: fgs } = await fgResp.json();
          allFgs.push(...(fgs ?? []));
        } catch {
          // skip
        }
      }
      setFeedbackGroups(allFgs);
    } catch (e) {
      console.error("Failed to fetch feedback groups", e);
    }
  }, []);

  useEffect(() => {
    fetchLesson();
    fetchFeedbackGroups();
  }, [fetchLesson, fetchFeedbackGroups]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const markDirty = () => setDirty(true);

  const handleSave = async () => {
    setSaving(true);
    try {
      let data;
      if (isNew && createState) {
        // 1. Create the lesson shell
        const createResp = await postJson(
          `${ADMIN}/curricula/${createState.curriculumId}/units/${createState.unitId}/lessons`,
          { title, type }
        );
        const { data: created } = await createResp.json();
        // 2. Save full content (nodes) via PUT
        const putResp = await putJson(`${ADMIN}/lessons/${created.id}`, {
          title,
          type,
          nodes,
        });
        ({ data } = await putResp.json());
        // 3. Navigate to real lesson URL so subsequent saves use PUT
        navigate(`/lessons/${data.id}/edit`, { replace: true });
      } else {
        if (!lessonId) return;
        const resp = await putJson(`${ADMIN}/lessons/${lessonId}`, {
          title,
          type,
          nodes,
        });
        ({ data } = await resp.json());
      }
      setLesson(data);
      setNodes(data.nodes ?? []);
      setDirty(false);
      setSnackbar({
        open: true,
        message: isNew ? "Created!" : `Saved (v${data.version})`,
        severity: "success",
      });
    } catch (e: any) {
      const detail = e?.error?.detail ?? "Failed to save";
      setSnackbar({ open: true, message: detail, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = (questionType: string) => {
    const node = EMPTY_NODE();
    node.question_type = questionType;
    if (questionType === "RANK") {
      node.options = node.options.map((o) => ({ ...o, is_correct: undefined }));
    }
    setNodes((prev) => [...prev, node]);
    setExpandedNode(node.id);
    markDirty();
  };

  const handleUpdateNode = (index: number, updated: QuestionNode) => {
    setNodes((prev) => prev.map((n, i) => (i === index ? updated : n)));
    markDirty();
  };

  const handleDeleteNode = (index: number) => {
    if (!window.confirm("Delete this question?")) return;
    setNodes((prev) => prev.filter((_, i) => i !== index));
    markDirty();
  };

  const handleMoveNode = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= nodes.length) return;
    setNodes((prev) => {
      const copy = [...prev];
      [copy[index], copy[newIndex]] = [copy[newIndex], copy[index]];
      return copy;
    });
    markDirty();
  };

  const handleToggleStatus = async () => {
    if (!lesson) return;
    const action = lesson.status === "ACTIVE" ? "disable" : "enable";
    try {
      const resp = await postJson(
        `${ADMIN}/lessons/${lesson.id}/${action}`,
        {}
      );
      const { data } = await resp.json();
      setLesson(data);
    } catch (e) {
      console.error("Failed to toggle lesson status", e);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!lesson && !isNew) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Lesson not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title={isNew ? `New: ${title}` : `Edit: ${title}`} />

      {/* Header with save */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          py: 1,
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {isNew ? "New Lesson" : "Lesson Editor"}
        </Typography>
        {lesson && (
          <>
            <Chip
              label={`v${lesson.version}`}
              size="small"
              variant="outlined"
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2">
                {lesson.status === "ACTIVE" ? "Active" : "Disabled"}
              </Typography>
              <Switch
                size="small"
                checked={lesson.status === "ACTIVE"}
                onChange={handleToggleStatus}
              />
            </Box>
          </>
        )}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          color={dirty ? "primary" : "inherit"}
        >
          {saving ? "Saving..." : "Save"}
          {dirty && " *"}
        </Button>
      </Box>

      {/* Metadata */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              markDirty();
            }}
            fullWidth
            size="small"
            placeholder="e.g., What is Credit"
          />
          <TextField
            label="Type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              markDirty();
            }}
            select
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="STANDARD">Standard</MenuItem>
            <MenuItem value="REVIEW">Review</MenuItem>
            <MenuItem value="UNIT_QUIZ">Unit Quiz</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Questions List */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Questions ({nodes.length})
        </Typography>

        {nodes.map((node, index) => (
          <QuestionCard
            key={node.id}
            node={node}
            index={index}
            totalCount={nodes.length}
            expanded={expandedNode === node.id}
            feedbackGroups={feedbackGroups}
            onToggle={() =>
              setExpandedNode(expandedNode === node.id ? null : node.id)
            }
            onChange={(updated) => handleUpdateNode(index, updated)}
            onDelete={() => handleDeleteNode(index)}
            onMoveUp={() => handleMoveNode(index, "up")}
            onMoveDown={() => handleMoveNode(index, "down")}
          />
        ))}

        {nodes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No questions yet. Add one below.
          </Typography>
        )}
      </Box>

      {/* Add Question Buttons */}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add Question
      </Typography>
      <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }} icon={false}>
        <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>
          <strong>Multiple Choice</strong> — User picks one correct answer
          <br />
          <strong>Multiple Correct</strong> — User picks all correct answers
          <br />
          <strong>Rank</strong> — User drags options into the right order
          <br />
          <strong>Fill in Blank</strong> — Sentence with [[BLANK]] slots; each
          blank maps to one correct option
        </Typography>
      </Alert>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => handleAddQuestion("MULTIPLE_CHOICE")}
        >
          Multiple Choice
        </Button>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => handleAddQuestion("MULTIPLE_CORRECT")}
        >
          Multiple Correct
        </Button>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => handleAddQuestion("RANK")}
        >
          Rank
        </Button>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => handleAddQuestion("FILL_IN_BLANK")}
        >
          Fill in Blank
        </Button>
      </Box>

      {/* Lesson Preview */}
      {nodes.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Lesson Preview
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {title || "(Untitled)"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              {type} &middot; {nodes.length} question
              {nodes.length !== 1 ? "s" : ""}
            </Typography>

            {nodes.map((node, idx) => (
              <Card key={node.id} sx={{ mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {idx + 1}. {node.question_text || "(empty question)"}
                  </Typography>
                  <Chip
                    label={node.question_type.replace(/_/g, " ")}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />

                  {node.question_type === "FILL_IN_BLANK" ? (
                    <Box sx={{ pl: 2 }}>
                      {(node.blanks ?? []).map((blank) => {
                        const correctOpt = node.options.find(
                          (o) => o.id === blank.correct_option_id
                        );
                        return (
                          <Typography
                            key={blank.id}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            [[{blank.id}]] &rarr;{" "}
                            <strong>
                              {correctOpt?.text || blank.correct_option_id}
                            </strong>
                          </Typography>
                        );
                      })}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        Options:{" "}
                        {node.options.map((o) => o.text || o.id).join(", ")}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ pl: 2 }}>
                      {node.options.map((opt, optIdx) => (
                        <Box
                          key={opt.id}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {node.question_type === "RANK" ? (
                            <Typography
                              variant="body2"
                              sx={{ minWidth: 20, color: "text.secondary" }}
                            >
                              {optIdx + 1}.
                            </Typography>
                          ) : opt.is_correct ? (
                            <CheckCircleIcon
                              sx={{
                                fontSize: 18,
                                color: "success.main",
                                mt: "2px",
                              }}
                            />
                          ) : (
                            <CancelIcon
                              sx={{
                                fontSize: 18,
                                color: "error.main",
                                mt: "2px",
                              }}
                            />
                          )}
                          <Box>
                            <Typography variant="body2">
                              {opt.text || "(empty)"}
                            </Typography>
                            {opt.explanation && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                {opt.explanation}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {node.explanation && (
                    <Typography
                      variant="caption"
                      color="info.main"
                      sx={{ mt: 1, display: "block", pl: 2 }}
                    >
                      Explanation: {node.explanation}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
